(() => {
  'use strict';
  if (customElements.get('gb-power-test-draft')) return;
  class GbPowerTestDraft extends HTMLElement {
    connectedCallback() {
      if (this.initialized) { if (this.observer) this.observer.observe(this); return; }
      this.initialized = true;
      const root = this;
  const { series, copy, locale, temperatures } = JSON.parse(root.querySelector('[data-chart-data]').textContent);
  const graphKeys = ['power', 'caliper', 'friction'], seriesKeys = Object.keys(series);
  const numbers = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  const fractions = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const ticks = { power: [0, 500, 1000, 1500, 2000], caliper: [20, 30, 40, 50, 60, 70], friction: [.2, .3, .4, .5, .6, .7, .8] };
  const params = new URLSearchParams(location.search);
  let active = graphKeys.includes(params.get('gb_power_graph')) ? params.get('gb_power_graph') : 'power';
  let highlight = seriesKeys.includes(params.get('gb_power_series')) ? params.get('gb_power_series') : 'power_compound';
  let selectedTemperature = Number(params.get('gb_power_temp')) || 300;
  const elements = {}, tabs = Array.from(root.querySelectorAll('[role="tab"]'));
  const selectors = Array.from(root.querySelectorAll('[data-highlight]'));
  const replace = (text, values) => text.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? '');
  const graphFor = key => series.ultimate_post650.graphs[key];
  function nearest(value) {
    return temperatures.reduce((best, t, i) => Math.abs(t - value) < Math.abs(temperatures[best] - value) ? i : best, 0);
  }
  function dimensions(width) {
    const w = Math.max(260, width || 900);
    return { w, h: w < 600 ? 280 : 300, left: 68, right: 18, top: 16, bottom: 52 };
  }
  function position(graph, layout, point) {
    return { x: layout.left + (point.x - 25) / 300 * (layout.w - layout.left - layout.right),
      y: layout.top + (graph.yDomain[1] - point.y) / (graph.yDomain[1] - graph.yDomain[0]) * (layout.h - layout.top - layout.bottom) };
  }
  function element(tag, attrs = {}, value) {
    const node = document.createElementNS('http://www.w3.org/2000/svg', tag);
    Object.entries(attrs).forEach(([key, item]) => node.setAttribute(key, String(item)));
    if (value !== undefined) node.textContent = value;
    return node;
  }
  function valueText(key, value) {
    return (key === 'friction' ? fractions.format(value) : numbers.format(value)) + (key === 'friction' ? '' : ' ' + graphFor(key).unit);
  }
  function select(key, index, announce = false) {
    const el = elements[key], graph = graphFor(key);
    index = Math.max(0, Math.min(temperatures.length - 1, Math.round(Number(index)) || 0));
    el.index = index;
    selectedTemperature = temperatures[index];
    const crossX = position(graph, el.layout, { x: selectedTemperature, y: graph.yDomain[0] }).x;
    el.vertical.setAttribute('x1', crossX); el.vertical.setAttribute('x2', crossX);
    el.vertical.setAttribute('y1', el.layout.top); el.vertical.setAttribute('y2', el.layout.h - el.layout.bottom);
    el.rotor.textContent = '≈ ' + numbers.format(selectedTemperature) + ' °C';
    let spoken = copy.rotorTemperatureShort + ': ≈ ' + numbers.format(selectedTemperature) + ' °C';
    seriesKeys.forEach(sid => {
      const point = series[sid].graphs[key].points.find(p => p.x === selectedTemperature);
      const marker = el.markers[sid], reading = el.readings[sid];
      marker.setAttribute('visibility', point ? 'visible' : 'hidden');
      if (point) { const p = position(graph, el.layout, point); marker.setAttribute('cx', p.x); marker.setAttribute('cy', p.y); }
      reading.textContent = point ? '≈ ' + valueText(key, point.y) : '— ' + copy.noData;
      reading.classList.toggle('missing', !point);
      const dim = highlight !== 'all' && highlight !== sid;
      el.paths[sid].classList.toggle('dimmed', dim);
      if (el.outlines[sid]) el.outlines[sid].classList.toggle('dimmed', dim);
      marker.classList.toggle('dimmed', dim);
      el.results[sid].classList.toggle('dimmed', dim);
      spoken += '; ' + copy.compounds[sid] + ': ' + (point ? '≈ ' + valueText(key, point.y) : copy.noData);
    });
    el.slider.value = index;
    el.slider.setAttribute('aria-valuetext', spoken);
    el.previous.disabled = index === 0; el.next.disabled = index === temperatures.length - 1;
    el.status.textContent = replace(copy.pointStatus, { index: index + 1, count: temperatures.length });
    if (announce) el.announcement.textContent = spoken;
    root.querySelectorAll('[data-temperature]').forEach(row => row.classList.toggle('current', Number(row.dataset.temperature) === selectedTemperature && (highlight === 'all' || row.dataset.series === highlight)));
  }
  function render(key) {
    const graph = graphFor(key), el = elements[key];
    el.layout = dimensions(el.wrap.clientWidth);
    const l = el.layout, xEnd = l.w - l.right, yEnd = l.h - l.bottom;
    el.svg.setAttribute('viewBox', `0 0 ${l.w} ${l.h}`); el.svg.style.height = l.h + 'px';
    const group = el.svg.querySelector('.drawn-plot'); group.replaceChildren();
    ticks[key].forEach(tick => {
      const p = position(graph, l, { x: 25, y: tick });
      group.append(element('line', { x1: l.left, x2: xEnd, y1: p.y, y2: p.y, class: 'gridline' }));
      group.append(element('text', { x: l.left - 9, y: p.y + 4, 'text-anchor': 'end' }, key === 'friction' ? fractions.format(tick) : numbers.format(tick)));
    });
    (l.w < 600 ? [50, 150, 250, 315] : [50, 100, 150, 200, 250, 300]).forEach(tick => {
      const p = position(graph, l, { x: tick, y: graph.yDomain[0] });
      group.append(element('line', { x1: p.x, x2: p.x, y1: yEnd, y2: yEnd + 5, class: 'axis-line' }));
      group.append(element('text', { x: p.x, y: yEnd + 22, 'text-anchor': 'middle' }, numbers.format(tick)));
    });
    group.append(element('line', { x1: l.left, x2: l.left, y1: l.top, y2: yEnd, class: 'axis-line' }));
    group.append(element('line', { x1: l.left, x2: xEnd, y1: yEnd, y2: yEnd, class: 'axis-line' }));
    group.append(element('text', { x: (l.left + xEnd) / 2, y: l.h - 6, 'text-anchor': 'middle' }, copy.rotorTemperature));
    group.append(element('text', { transform: `translate(13 ${(l.top + yEnd) / 2}) rotate(-90)`, 'text-anchor': 'middle' }, copy.graphs[key].axis));
    el.paths = {}; el.outlines = {}; el.markers = {};
    el.vertical = element('line', { class: 'crosshair' }); group.append(el.vertical);
    seriesKeys.forEach(sid => {
      const path = series[sid].graphs[key].points.map((point, i) => { const p = position(graph, l, point); return `${i ? 'L' : 'M'}${p.x.toFixed(2)} ${p.y.toFixed(2)}`; }).join(' ');
      if (sid === 'power_compound') { el.outlines[sid] = element('path', { d: path, class: 'curve-outline ' + sid }); group.append(el.outlines[sid]); }
      el.paths[sid] = element('path', { d: path, class: 'curve ' + sid }); group.append(el.paths[sid]);
      el.markers[sid] = element('circle', { r: 5, class: 'point-marker ' + sid });
    });
    seriesKeys.forEach(sid => group.append(el.markers[sid]));
    select(key, nearest(selectedTemperature));
  }
  function activate(key, focus = false) {
    active = key;
    tabs.forEach(tab => {
      const chosen = tab.dataset.graph === key;
      tab.setAttribute('aria-selected', String(chosen)); tab.tabIndex = chosen ? 0 : -1;
      root.querySelector('[id="' + tab.getAttribute('aria-controls') + '"]').hidden = !chosen;
      if (chosen && focus) tab.focus();
    });
    render(key);
  }
  function setHighlight(sid) {
    highlight = sid;
    selectors.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.highlight === sid)));
    if (elements[active].layout) select(active, nearest(selectedTemperature), true);
  }
  for (const key of graphKeys) {
    const panel = root.querySelector('[data-graph-panel="' + key + '"]'), get = selector => panel.querySelector(selector);
    elements[key] = { panel, wrap: get('.plot-wrap'), svg: get('.plot'), slider: get('.point-slider'), rotor: get('[data-reading="rotor"]'),
      previous: get('[data-step="-1"]'), next: get('[data-step="1"]'), status: get('.point-status'), announcement: get('[role="status"]'), index: 0,
      readings: Object.fromEntries(seriesKeys.map(sid => [sid, get('[data-reading="' + sid + '"]')])),
      results: Object.fromEntries(seriesKeys.map(sid => [sid, get('[data-result="' + sid + '"]')])) };
    const el = elements[key];
    el.slider.max = temperatures.length - 1;
    el.slider.addEventListener('input', () => select(key, Number(el.slider.value)));
    el.slider.addEventListener('keydown', event => {
      const change = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 5, PageDown: -5 }[event.key];
      let index = change === undefined ? undefined : el.index + change;
      if (event.key === 'Home') index = 0;
      if (event.key === 'End') index = temperatures.length - 1;
      if (index !== undefined) { event.preventDefault(); select(key, index); }
    });
    [el.previous, el.next].forEach(button => button.addEventListener('click', () => select(key, el.index + Number(button.dataset.step), true)));
    const pointAtPointer = event => {
      const rect = el.svg.getBoundingClientRect(), l = el.layout;
      const localX = (event.clientX - rect.left) * l.w / rect.width;
      const temp = 25 + (localX - l.left) / (l.w - l.left - l.right) * 300;
      select(key, nearest(temp));
    };
    el.svg.addEventListener('pointerdown', pointAtPointer);
    el.svg.addEventListener('pointermove', event => { if (event.pointerType !== 'touch' || event.buttons) pointAtPointer(event); });
  }
  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => activate(tab.dataset.graph));
    tab.addEventListener('keydown', event => {
      let next;
      if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      if (next !== undefined) { event.preventDefault(); activate(tabs[next].dataset.graph, true); }
    });
  });
  selectors.forEach(button => button.addEventListener('click', () => setHighlight(button.dataset.highlight)));
  setHighlight(highlight); activate(active);
  if (typeof ResizeObserver === 'function') { this.observer = new ResizeObserver(() => render(active)); this.observer.observe(this); }
    }
    disconnectedCallback() { if (this.observer) this.observer.disconnect(); }
  }
  customElements.define('gb-power-test-draft', GbPowerTestDraft);
})();
